import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import useSEOMeta from '@/hooks/useSEOMeta';
import { testimonials, aboutStats } from '@/mocks/testimonials';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useSEOMeta({
    title: '关于我们 - 三上悠亚粉丝站',
    description: '了解三上悠亚粉丝站的品牌故事、行业口碑与联系方式。我们致力于为三上悠亚粉丝提供最新、最全面的资讯与互动平台。',
    keywords: '三上悠亚,三上悠亚粉丝站,关于我们,三上悠亚联系方式,三上悠亚口碑',
    canonicalUrl: 'https://cdrmjj.cn/about',
    ogType: 'website',
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    setIsSubmitting(true);
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const resp = await fetch('https://readdy.ai/api/form/d9ssv658o1vn02ekulqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await resp.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (resp.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed as { meta?: { message?: string } }).meta?.message ||
          (parsed as { message?: string }).message ||
          (parsed as { meta?: { detail?: string } }).meta?.detail ||
          responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于三上悠亚粉丝站',
    description: '了解三上悠亚粉丝站的品牌故事、行业口碑与联系方式。',
    url: 'https://cdrmjj.cn/about',
    inLanguage: 'zh-CN',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '三上悠亚粉丝站是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '三上悠亚粉丝站是一个专注于日本知名女演员与偶像三上悠亚的官方粉丝资讯平台，汇集最新动态、作品资讯、个人经历及粉丝互动内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何获取三上悠亚的最新动态？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过订阅我们的资讯邮件，或定期访问本站的"最新动态"分类页面，第一时间获取三上悠亚的最新资讯。',
        },
      },
      {
        '@type': 'Question',
        name: '粉丝站的内容更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们保持每日更新，确保粉丝能及时了解三上悠亚的最新消息、作品发布及活动动态。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系粉丝站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系表单与我们取得联系，或通过社交媒体平台关注我们。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          {/* Hero */}
          <section className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <div className="mt-6 relative w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20elegant%20abstract%20background%20with%20soft%20coral%20and%20cream%20gradients%2C%20gentle%20flowing%20shapes%2C%20Japanese%20zen%20aesthetic%2C%20minimalist%20composition%2C%20warm%20atmospheric%20lighting%2C%20large%20empty%20center%20space%2C%20sophisticated%20editorial%20quality&width=1600&height=600&seq=mikami-about-hero&orientation=landscape"
                alt="关于三上悠亚粉丝站"
                title="关于我们 - 三上悠亚粉丝站"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-white">关于我们</h1>
                <p className="mt-3 text-sm md:text-base text-white/80 max-w-lg leading-relaxed">
                  了解三上悠亚粉丝站的品牌故事与使命
                </p>
              </div>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  品牌故事
                </h2>
                <div className="mt-6 space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    三上悠亚粉丝站创立于2018年，是中文互联网上最早专注于三上悠亚资讯的粉丝平台之一。我们由一群热爱三上悠亚的粉丝自发创建，致力于为广大中文粉丝提供一个全面、及时、高质量的信息交流平台。
                  </p>
                  <p>
                    从最初的个人博客到如今的专业粉丝资讯站，我们始终坚持"真实、全面、用心"的内容理念。每一次资讯更新、每一篇深度报道、每一个互动活动，都倾注了我们对三上悠亚的热爱和对粉丝社区的承诺。
                  </p>
                  <p>
                    今天，三上悠亚粉丝站已经成为中文圈最具影响力的三上悠亚资讯平台之一，月访问量超过50万次，累积服务了数百万粉丝用户。
                  </p>
                </div>

                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {aboutStats.map((stat) => (
                    <div key={stat.label} className="text-center p-4 rounded-lg bg-accent-50 border border-accent-100/60">
                      <div className="text-2xl font-heading font-bold text-accent-600">{stat.value}</div>
                      <div className="mt-1 text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20minimalist%20studio%20workspace%20with%20soft%20natural%20lighting%2C%20plant%20decor%2C%20clean%20wooden%20desk%2C%20Japanese%20aesthetic%20interior%2C%20warm%20beige%20and%20cream%20tones%2C%20calm%20atmosphere%2C%20editorial%20photography&width=800&height=600&seq=mikami-about-story&orientation=landscape"
                  alt="品牌故事"
                  title="三上悠亚粉丝站品牌故事"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Mission */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-20">
            <div className="bg-background-100/70 rounded-2xl border border-background-200/60 p-10 md:p-14">
              <div className="max-w-3xl mx-auto text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-heart-line text-2xl"></i>
                </div>
                <h2 className="mt-5 text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  我们的使命
                </h2>
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                  我们致力于打造中文互联网上最全面、最及时、最温暖的三上悠亚粉丝社区。
                  让每一位喜欢三上悠亚的粉丝，无论身在何处，都能在这里找到归属感，获取最新资讯，
                  分享对偶像的热爱。我们不只是一家资讯站，更是一个由粉丝创建、为粉丝服务的温暖家园。
                </p>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="p-5 rounded-xl bg-background-50 border border-background-200/50">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-shield-check-line text-lg"></i>
                    </div>
                    <h3 className="mt-3 text-sm font-heading font-semibold text-foreground-900">真实可靠</h3>
                    <p className="mt-1 text-xs text-foreground-500 leading-relaxed">严格核实每一条资讯来源，确保信息准确无误</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-50 border border-background-200/50">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-flashlight-line text-lg"></i>
                    </div>
                    <h3 className="mt-3 text-sm font-heading font-semibold text-foreground-900">及时高效</h3>
                    <p className="mt-1 text-xs text-foreground-500 leading-relaxed">7×24小时追踪最新动态，第一时间为粉丝呈现</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-50 border border-background-200/50">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-user-heart-line text-lg"></i>
                    </div>
                    <h3 className="mt-3 text-sm font-heading font-semibold text-foreground-900">粉丝为本</h3>
                    <p className="mt-1 text-xs text-foreground-500 leading-relaxed">一切从粉丝需求出发，用心打造最佳体验</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-20">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                行业口碑
              </h2>
              <p className="mt-2 text-sm text-foreground-500">来自业内人士与粉丝的真实评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="p-6 rounded-xl bg-background-50 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
                  <div className="mt-4 pt-4 border-t border-background-200/60 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-heading font-bold text-sm">
                      {t.name[0]}
                    </div>
                    <div>
                      <div className="text-sm font-label font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-3xl mx-auto px-4 md:px-6 mt-20">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                常见问题
              </h2>
            </div>
            <div className="space-y-3">
              {[
                { q: '三上悠亚粉丝站是什么？', a: '三上悠亚粉丝站是一个专注于日本知名女演员与偶像三上悠亚的官方粉丝资讯平台，汇集最新动态、作品资讯、个人经历及粉丝互动内容。' },
                { q: '如何获取三上悠亚的最新动态？', a: '您可以通过订阅我们的资讯邮件，或定期访问本站的"最新动态"分类页面，第一时间获取三上悠亚的最新资讯。' },
                { q: '粉丝站的内容更新频率如何？', a: '我们保持每日更新，确保粉丝能及时了解三上悠亚的最新消息、作品发布及活动动态。' },
                { q: '如何联系粉丝站？', a: '您可以通过本页面的联系表单与我们取得联系，或通过社交媒体平台关注我们。' },
              ].map((item, index) => (
                <details key={index} className="group rounded-xl border border-background-200/60 bg-background-50 overflow-hidden">
                  <summary className="flex items-center justify-between cursor-pointer p-5 text-sm font-label font-medium text-foreground-900 list-none">
                    {item.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="max-w-2xl mx-auto px-4 md:px-6 mt-20 pb-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                联系我们
              </h2>
              <p className="mt-2 text-sm text-foreground-500">有任何问题或建议？欢迎给我们留言</p>
            </div>

            {formStatus === 'success' ? (
              <div className="text-center p-10 rounded-2xl bg-accent-50 border border-accent-200/60">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="mt-4 text-lg font-heading font-bold text-foreground-900">感谢您的留言！</h3>
                <p className="mt-2 text-sm text-foreground-600">我们会尽快回复您</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-5 px-6 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-full text-sm font-label font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送新留言
                </button>
              </div>
            ) : (
              <form
                data-readdy-form=""
                onSubmit={handleSubmit}
                className="space-y-5 p-8 rounded-2xl bg-background-50 border border-background-200/60"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                    您的姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入您的姓名"
                    className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                    电子邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入您的邮箱地址"
                    className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    placeholder="请输入留言主题"
                    className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请输入您的留言内容，最多500字"
                    className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                  ></textarea>
                  <p className="mt-1 text-xs text-foreground-400">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="contact-form-hp"
                />

                {formStatus === 'error' && formError && (
                  <div className="p-3 rounded-lg bg-accent-100/60 text-sm text-accent-700">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 bg-accent-500 hover:bg-accent-600 disabled:bg-accent-300 disabled:cursor-not-allowed text-background-50 rounded-lg text-sm font-label font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? '发送中...' : '发送留言'}
                </button>
              </form>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}